/* Variant replacement (drawer lot 2, 25 Oct. 2026) is two non-atomic mutations. Fail closed; never retry or roll back.
   Any ordinary line qualifies: the exclusions apply to the source line only. Foreign lines (gifts and
   bundles included) must come out identical to the cent. A price change under a discount is predicted
   from the source allocations before the add; anything unpredictable is refused before any POST. */
(() => {
  if (window.K9CartVariant) return;
  const sorted = value => Array.isArray(value) ? value.map(sorted) : value && typeof value === 'object'
    ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, sorted(v)])) : value;
  const canonical = value => JSON.stringify(sorted(value));
  const properties = item => Object.fromEntries((Array.isArray(item.properties) ? item.properties : Object.entries(item.properties || {})).filter(([, v]) => v !== ''));
  const identity = item => canonical([Number(item.id), properties(item), item.selling_plan_allocation?.selling_plan?.id || null]);
  const quantities = items => {
    const result = new Map();
    for (const item of items) result.set(identity(item), (result.get(identity(item)) || 0) + item.quantity);
    return [...result].sort(([a], [b]) => a.localeCompare(b));
  };
  const meta = cart => canonical([cart.currency, cart.attributes || {}, cart.note || '', cart.discount_codes || [], cart.cart_level_discount_applications || []]);
  // Shopify regenerates application.key / created_at on a swap: keep the full financial meaning, not request IDs.
  const fields = ['type', 'title', 'value', 'value_type', 'allocation_method', 'target_selection', 'target_type'];
  const application = (allocation, withTotal) => {
    const app = allocation.discount_application;
    if (!Number.isSafeInteger(allocation.amount) || allocation.amount < 0 || !app || typeof app !== 'object') throw Error('allocation');
    return Object.fromEntries((withTotal ? [...fields, 'total_allocated_amount'] : fields).map(key => [key, app[key] ?? null]));
  };
  // total_allocated_amount sums the application over every line: it follows the swapped line when prices differ.
  const allocations = (item, withTotal = true) => (item.line_level_discount_allocations || [])
    .map(allocation => canonical([allocation.amount, application(allocation, withTotal)])).sort();
  // Bundle parents carry their components: a foreign bundle whose content changes is a changed line.
  const components = item => (item.item_components || []).map(c => canonical([Number(c.id ?? c.variant_id) || null, c.quantity ?? null, properties(c),
    c.original_price ?? null, c.final_price ?? null, c.original_line_price ?? null, c.final_line_price ?? null])).sort();
  const financial = (item, withTotal = true) => canonical([identity(item), item.quantity, item.original_price, item.final_price,
    item.original_line_price, item.final_line_price, item.total_discount || 0, allocations(item, withTotal),
    Boolean(item.has_components || item.item_components?.length), components(item)]);
  const discountedLine = item => item.final_line_price !== item.original_line_price || item.total_discount || item.line_level_discount_allocations?.length;
  function validCart(cart) {
    if (!cart || !Array.isArray(cart.items) || !cart.currency || !Number.isSafeInteger(cart.total_price)) throw Error('cart');
    let total = 0;
    for (const item of cart.items) {
      if (!Number.isSafeInteger(item.id) || !Number.isSafeInteger(item.quantity) || item.quantity < 1 || !item.key
        || !Number.isSafeInteger(item.original_price) || item.original_price < 0
        || !Number.isSafeInteger(item.final_price) || item.final_price < 0 || item.final_price > item.original_price
        || item.original_line_price !== item.original_price * item.quantity
        || !Number.isSafeInteger(item.final_line_price) || item.final_line_price < 0 || item.final_line_price > item.original_line_price) throw Error('unsupported-cart');
      total += item.final_line_price;
    }
    if (cart.cart_level_discount_applications?.length || total !== cart.total_price
      || (cart.total_discount || 0) !== cart.items.reduce((sum, i) => sum + i.original_line_price - i.final_line_price, 0)) throw Error('unsupported-cart');
    return cart;
  }
  // Exclusions of the line being replaced; the Liquid shows "Modifier" under the same rules.
  const ordinary = item => !(item.item_components?.length || item.has_components || item.selling_plan_allocation
    || item.properties?._k9_cadeau || item.properties?._oid
    || item.instructions?.can_update_quantity === false || item.instructions?.can_remove === false
    || item.original_price <= 0 || item.final_price <= 0);
  // Variants of the line's product, read when the editor opens. Cents, as /products/<handle>.js returns them.
  function catalogue(product, config) {
    if (!product || Number(product.id) !== config.productId || !Array.isArray(product.variants) || !product.variants.length) throw Error('product');
    if (product.requires_selling_plan) throw Error('scope');
    const names = (product.options || []).map(option => typeof option === 'string' ? option : option?.name);
    if (!names.length || names.some(name => typeof name !== 'string')) throw Error('product');
    const variants = product.variants.map(v => {
      const options = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].slice(0, names.length);
      if (!Number.isSafeInteger(v.id) || !Number.isSafeInteger(v.price) || v.price <= 0 || typeof v.available !== 'boolean'
        || options.length !== names.length || options.some(value => typeof value !== 'string')) throw Error('product');
      return { id: v.id, price: v.price, available: v.available, options };
    });
    if (!variants.some(v => v.id === config.variantId)) throw Error('product');
    const values = names.map((_, i) => [...new Set(variants.map(v => v.options[i]))]);
    return { names, values, variants };
  }
  // Expected allocation of each source application on the target line, decided before any POST.
  function predict(source, current, target) {
    return (source.line_level_discount_allocations || []).map(allocation => {
      const app = allocation.discount_application;
      const meaning = canonical(application(allocation, false));
      if (app?.value_type === 'percentage') {
        return { meaning, amount: Math.round(allocation.amount * target.price / current.price), tolerance: source.quantity };
      }
      if (app?.value_type === 'fixed_amount' && app.allocation_method === 'each') {
        // The source allocation may already be capped by the cheaper source price: use the rule's value.
        const unit = Math.round(Number(app.value) * 100);
        if (!Number.isSafeInteger(unit) || unit < 0) throw Error('discounted-scope');
        return { meaning, amount: Math.min(unit * source.quantity, target.price * source.quantity), tolerance: 0 };
      }
      throw Error('discounted-scope');
    }).sort((a, b) => a.meaning.localeCompare(b.meaning) || a.amount - b.amount);
  }
  function plan(config, targetId, cart) {
    validCart(cart);
    if (cart.currency !== config.currency || !Array.isArray(config.variants)) throw Error('scope');
    const source = cart.items.find(i => i.key === config.key);
    if (!source || source.id !== config.variantId || source.product_id !== config.productId
      || source.quantity !== config.quantity || canonical(properties(source)) !== canonical(properties({ properties: config.properties }))
      || cart.items.filter(i => identity(i) === identity(source)).length !== 1) throw Error('stale');
    if (!ordinary(source)) throw Error('unsupported-cart');
    const target = config.variants.find(v => v.id === Number(targetId));
    const current = config.variants.find(v => v.id === source.id);
    if (!target?.available || !current || target.id === source.id || current.price !== source.original_price
      || !Number.isSafeInteger(target.price) || target.price <= 0) throw Error('variant');
    const discounted = cart.items.some(discountedLine);
    const priceChange = target.price !== current.price;
    const addition = { id: target.id, quantity: source.quantity, properties: properties(source) };
    if (discounted && cart.items.some(i => identity(i) === identity(addition))) throw Error('discounted-scope');
    const expectedAllocations = discounted && priceChange ? predict(source, current, target) : null;
    return { source, target, addition, discounted, priceChange, expectedAllocations,
      finalFinancial: cart.items.map(i => financial(i === source ? { ...i, id: target.id } : i)).sort(), original: cart, metadata: meta(cart), sourceIdentity: identity(source), targetIdentity: identity(addition),
      intermediate: quantities([...cart.items, addition]), final: quantities(cart.items.flatMap(i => i === source ? [addition] : [i])),
      intermediateTotal: cart.total_price + target.price * source.quantity,
      finalTotal: cart.total_price + (target.price - source.original_price) * source.quantity, finalDiscount: cart.total_discount || 0,
      foreign: cart.items.filter(i => identity(i) !== identity(source) && identity(i) !== identity(addition)).map(i => financial(i)).sort(),
      foreignLines: cart.items.filter(i => i !== source).map(i => financial(i, false)).sort() };
  }
  function predicted(cart, expected) {
    if (canonical(cart.items.filter(i => identity(i) !== expected.targetIdentity).map(i => financial(i, false)).sort()) !== canonical(expected.foreignLines)) throw Error('changed');
    const lines = cart.items.filter(i => identity(i) === expected.targetIdentity);
    if (lines.length !== 1 || lines[0].quantity !== expected.source.quantity || lines[0].original_price !== expected.target.price) throw Error('changed');
    const actual = (lines[0].line_level_discount_allocations || [])
      .map(allocation => ({ meaning: canonical(application(allocation, false)), amount: allocation.amount }))
      .sort((a, b) => a.meaning.localeCompare(b.meaning) || a.amount - b.amount);
    if (actual.length !== expected.expectedAllocations.length || actual.some((a, i) => a.meaning !== expected.expectedAllocations[i].meaning
      || Math.abs(a.amount - expected.expectedAllocations[i].amount) > expected.expectedAllocations[i].tolerance)) throw Error('discount');
  }
  function verify(cart, expected, phase) {
    validCart(cart);
    // While both variants coexist, Shopify may apply a volume or pair discount to the temporary
    // second unit: final prices and totals are only proven in the final phase.
    if (meta(cart) !== expected.metadata || canonical(quantities(cart.items)) !== canonical(expected[phase])
      || (phase === 'final' && !expected.discounted && cart.total_price !== expected.finalTotal)
      || (phase === 'final' && expected.discounted && !expected.priceChange && (cart.total_price !== expected.finalTotal || (cart.total_discount || 0) !== expected.finalDiscount || canonical(cart.items.map(i => financial(i)).sort()) !== canonical(expected.finalFinancial)))
      || (phase === 'final' && !expected.discounted && canonical(cart.items.filter(i => identity(i) !== expected.sourceIdentity && identity(i) !== expected.targetIdentity).map(i => financial(i)).sort()) !== canonical(expected.foreign))) throw Error('changed');
    if (phase === 'final' && expected.discounted && expected.priceChange) predicted(cart, expected);
    for (const item of cart.items) {
      if (identity(item) === expected.sourceIdentity && item.original_price !== expected.source.original_price) throw Error('source-price');
      if (identity(item) === expected.targetIdentity && item.original_price !== expected.target.price) throw Error('target-price');
    }
    return cart;
  }
  async function replace(config, targetId, io) {
    let cart, posted = false, phase = 'before', skipRecoveryRead = false, expected;
    try {
      cart = await io.read();
      expected = plan(config, targetId, cart);
      await io.beforeMutation?.();
      posted = true; phase = 'add';
      const added = await io.add(expected.addition);
      if (!added.response.ok) {
        skipRecoveryRead = added.response.status === 429 || added.response.status >= 500;
        if (added.response.status === 422) {
          // Refused add (stock, limits): a fresh cart equal to the original one means nothing was posted.
          const fresh = await io.read();
          validCart(fresh);
          if (meta(fresh) === expected.metadata && canonical(fresh.items.map(i => financial(i)).sort()) === canonical(expected.original.items.map(i => financial(i)).sort())) {
            return { success: false, posted: false, phase, cart: fresh, reason: 'add-refused' };
          }
        }
        throw Error('add');
      }
      cart = verify(await io.read(), expected, 'intermediate');
      // A second fresh read catches cart edits occurring after the add response.
      cart = verify(await io.read(), expected, 'intermediate');
      const old = cart.items.filter(i => identity(i) === expected.sourceIdentity);
      if (old.length !== 1 || old[0].quantity !== expected.source.quantity || !old[0].key) throw Error('source-key');
      await io.beforeMutation?.();
      phase = 'remove';
      const removed = await io.remove(old[0].key);
      if (!removed.response.ok) { skipRecoveryRead = removed.response.status === 429 || removed.response.status >= 500; throw Error('remove'); }
      cart = verify(await io.read(), expected, 'final');
      return { success: true, posted, phase, cart };
    } catch (error) {
      // Reads establish reality only. Never issue another mutation after an error.
      skipRecoveryRead ||= error.status === 429 || error.status >= 500;
      if (skipRecoveryRead) cart = null;
      if (posted && !skipRecoveryRead) { try { cart = await io.read(); } catch { cart = null; } }
      return { success: false, posted, phase, cart, reason: error.message };
    }
  }
  // Editor state of a selection: the variant it names and why it cannot be validated.
  function resolve(model, selection, cart, config) {
    const variant = model.variants.find(v => v.options.every((value, i) => value === selection[i]));
    if (!variant) return { variant: null, reason: 'missing' };
    if (variant.id === config.variantId) return { variant, reason: 'current' };
    if (!variant.available) return { variant, reason: 'unavailable' };
    const wanted = canonical([variant.id, properties({ properties: config.properties }), null]);
    if (cart?.items?.some(i => identity(i) === wanted)) return { variant, reason: 'in-cart' };
    return { variant, reason: null };
  }
  const status = { blocked: false, navigating: false };
  window.K9CartVariant = { canonical, identity, quantities, validCart, catalogue, plan, verify, replace, resolve, status };
  function register() {
    if (!window.K9NativeOffer || customElements.get('k9-cart-line-variant')) return;
    const { Base, state } = window.K9NativeOffer;
    const syncControls = () => document.querySelectorAll('k9-cart-line-variant').forEach(e => e.syncControls?.());
    const refresh = () => {
      document.querySelectorAll('k9-patch-offer,k9-product-offer,k9-fixed-offer,k9-gift-offer,k9-bogo-offer,k9-paliers-offer').forEach(e => { if (e.config) e.update(); });
      syncControls();
    };
    const checkout = '#CartDrawer-Checkout,#checkout,[name="checkout"],a[href^="/checkout"]';
    const express = '[data-k9-gift-express],.additional-checkout-buttons,shopify-accelerated-checkout,shopify-accelerated-checkout-cart';
    const mutations = 'cart-remove-button,.quantity__button,.quantity__input,#CartDrawer-Undo button,[data-variant-toggle],[data-variant-validate],.k9-variant-editor input,[data-submit],k9-patch-offer input,k9-product-offer input,k9-fixed-offer input,k9-gift-offer input,k9-bogo-offer input,gp-product-button button,button[name="add"]';
    let guardObserver;
    const enforceCheckout = () => {
      document.querySelectorAll(checkout).forEach(b => {
        if (b.getAttribute('aria-disabled') !== 'true') b.setAttribute('aria-disabled', 'true');
        if ('disabled' in b && !b.disabled) b.disabled = true;
      });
      document.querySelectorAll(express).forEach(e => { if (!e.hidden) e.hidden = true; if (!e.hasAttribute('inert')) e.setAttribute('inert', ''); });
    };
    const block = () => {
      document.documentElement.setAttribute('data-k9-variant-checkout-blocked', '');
      if (!document.getElementById('k9-variant-checkout-guard')) {
        // Gift reconciliation owns hidden/disabled too. An independent ancestor guard
        // must survive its later release, including wallets inside closed shadow roots.
        const style = document.createElement('style'); style.id = 'k9-variant-checkout-guard';
        style.textContent = express.split(',').map(s => 'html[data-k9-variant-checkout-blocked] ' + s).join(',') + '{display:none!important}'
          + checkout.split(',').map(s => 'html[data-k9-variant-checkout-blocked] ' + s).join(',') + '{opacity:.45!important;cursor:not-allowed!important}';
        document.head.append(style);
      }
      enforceCheckout();
      if (!guardObserver && typeof MutationObserver !== 'undefined') {
        guardObserver = new MutationObserver(() => { if (status.blocked || status.navigating) enforceCheckout(); });
        guardObserver.observe(document.documentElement, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'hidden', 'inert'] });
      }
      syncControls();
    };
    for (const type of ['click', 'submit', 'change', 'keydown']) document.addEventListener(type, event => {
      if (type === 'keydown' && !['Enter', ' ', 'Spacebar'].includes(event.key)) return;
      const path = event.composedPath?.() || [event.target];
      const checkoutAction = path.some(node => node.closest?.(checkout + ',' + express)) || event.submitter?.matches(checkout);
      const staleMutation = (status.blocked || status.navigating) && path.some(node => node.closest?.(mutations));
      if ((state.pending || status.blocked || status.navigating) && (checkoutAction || staleMutation
        || type === 'submit' && event.target.matches?.('form[action*="/cart"],form[action*="/checkout"]'))) {
        event.preventDefault(); event.stopImmediatePropagation();
      }
    }, true);
    const element = (tag, attributes = {}, text) => {
      const node = document.createElement(tag);
      for (const [key, value] of Object.entries(attributes)) if (value !== undefined && value !== null) node.setAttribute(key, value);
      if (text !== undefined) node.textContent = text;
      return node;
    };
    class CartLineVariant extends HTMLElement {
      connectedCallback() {
        this.events?.abort(); this.events = new AbortController();
        try { this.config = JSON.parse(this.querySelector('[data-variant-config]').textContent); }
        catch { return; }
        for (const [key, suffix] of [['cartUrl', '/cart.js'], ['addUrl', '/cart/add.js'], ['changeUrl', '/cart/change.js'], ['productUrl', '.js']]) {
          try { const url = new URL(this.config[key], location.origin); if (url.origin !== location.origin || !url.pathname.endsWith(suffix) || url.search || url.hash) return; } catch { return; }
        }
        this.toggle = this.querySelector('[data-variant-toggle]');
        const panelId = this.dataset.panel;
        const root = this.closest('.k9-cart-line') || this.parentElement;
        this.message = root?.querySelector(`[data-variant-status-for="${panelId}"]`);
        this.recovery = root?.querySelector(`[data-variant-recovery-for="${panelId}"]`);
        if (!this.toggle || !panelId || !root || !this.message || !this.recovery) return;
        this.panel = document.getElementById(panelId);
        this.validate = this.panel?.querySelector('[data-variant-validate]') || null;
        this.reason = this.panel?.querySelector('.k9-variant-editor__reason') || null;
        if (!this.panel) {
          this.panel = element('div', { id: panelId, class: 'k9-variant-editor', role: 'group', 'aria-label': this.config.text.panel, hidden: '' });
          root.append(this.panel);
        }
        this.toggle.hidden = false;
        this.syncControls();
        const signal = this.events.signal;
        this.toggle.addEventListener('click', event => {
          event.preventDefault();
          if (this.opening) return;
          this.panel.hidden ? void this.open() : this.close(true);
        }, { signal });
        // Delegated: a reconnection without redraw keeps the open panel and its Valider working.
        this.panel.addEventListener('click', event => { if (event.target.closest?.('[data-variant-validate]')) void this.change(); }, { signal });
        // The drawer form updates quantities on any bubbling change; the editor's choices are not quantities.
        this.panel.addEventListener('change', event => { event.stopPropagation(); this.choose(event.target); }, { signal });
        // The drawer closes on keyup Escape: the editor closes first and keeps the drawer open.
        this.panel.addEventListener('keyup', event => { if (event.key === 'Escape') { event.stopPropagation(); this.close(true); } }, { signal });
        // Enter on a choice must not submit the checkout form the editor sits in.
        this.panel.addEventListener('keydown', event => { if (event.key === 'Enter' && event.target.matches('input')) event.preventDefault(); }, { signal });
        if (status.blocked) { this.show(this.config.text.uncertain, true); block(); }
      }
      locked() { return state.pending || status.blocked || status.navigating; }
      syncControls() {
        if (!this.toggle) return;
        const locked = this.locked();
        this.toggle.disabled = locked;
        this.panel?.querySelectorAll('input').forEach(input => { input.disabled = locked; });
        this.updateValidate();
        if (!state.pending || status.blocked || status.navigating) { clearTimeout(this.pendingTimer); this.pendingTimer = null; return; }
        // A Patch/Bogo/Gift redraw connects us while their shared lock is held.
        // They do not know this element; wait only while that lock remains held.
        if (!this.pendingTimer && this.isConnected) this.pendingTimer = setTimeout(() => { this.pendingTimer = null; if (this.isConnected) this.syncControls(); }, 100);
      }
      disconnectedCallback() { this.events?.abort(); clearTimeout(this.pendingTimer); this.pendingTimer = null; }
      show(text, recovery) { this.message.textContent = text; this.message.hidden = !text; this.recovery.hidden = !recovery; }
      close(focus) {
        this.panel.hidden = true; this.panel.replaceChildren(); this.model = null;
        this.toggle.setAttribute('aria-expanded', 'false'); this.toggle.textContent = this.config.text.modify;
        if (focus) this.toggle.focus();
      }
      async open() {
        if (this.locked()) { this.show(this.config.text.wait, status.blocked); return; }
        this.opening = true; this.setAttribute('aria-busy', 'true'); this.show('', false);
        try {
          const [product, cart] = await Promise.all([Base.json(this.config.productUrl), Base.json(this.config.cartUrl)]);
          if (!product.response.ok || !cart.response.ok) throw Error('read');
          this.model = catalogue(product.data, this.config);
          this.cart = validCart(cart.data);
          if (!this.isConnected) return;
          this.selection = [...this.model.variants.find(v => v.id === this.config.variantId).options];
          this.render();
          this.panel.hidden = false;
          this.toggle.setAttribute('aria-expanded', 'true'); this.toggle.textContent = this.config.text.close;
          (this.panel.querySelector('input:checked') || this.panel.querySelector('input'))?.focus();
        } catch {
          this.show(this.config.text.beforeError, false);
          this.recovery.hidden = false;
        } finally { this.opening = false; this.removeAttribute('aria-busy'); }
      }
      render() {
        const { names, values } = this.model;
        const nodes = names.map((name, i) => {
          const swatches = this.config.swatches?.[name];
          const group = element('fieldset', { class: `k9-variant-editor__group${swatches ? ' k9-variant-editor__group--swatches' : ''}` });
          const legend = element('legend', { class: 'k9-variant-editor__legend' });
          group.append(legend);
          // Size guide of the product page, beside the « Taille » title (maquette v4); opened there on arrival.
          if (name === 'Taille' && this.config.sizeGuide) {
            const guide = element('a', { class: 'k9-variant-editor__guide', href: this.config.sizeGuide, target: '_blank', rel: 'noopener' }, this.config.text.sizeGuide);
            guide.append(element('span', { class: 'visually-hidden' }, ` ${this.config.text.newTab}`));
            group.append(guide);
          }
          // Short sizes share a grid of equal pills (maquette v4); long values (« S 80×20 cm ») keep their width.
          const grid = !swatches && values[i].every(value => value.length <= 4);
          const options = element('div', { class: `k9-variant-editor__options${grid ? ' k9-variant-editor__options--grid' : ''}` });
          values[i].forEach((value, j) => {
            const id = `${this.panel.id}-${i}-${j}`;
            const input = element('input', { type: 'radio', class: 'k9-variant-editor__input', id, name: `${this.panel.id}-${i}`, value, 'data-option': i });
            input.checked = value === this.selection[i];
            // The input fills its label: a tap anywhere on the pill or swatch is a tap on the radio.
            const label = element('label', { class: 'k9-variant-editor__choice' });
            label.append(input);
            if (swatches) {
              label.append(element('span', { class: 'k9-variant-editor__swatch', style: `--k9-swatch:${swatches[value] || 'transparent'}`, 'aria-hidden': 'true' }), element('span', { class: 'visually-hidden' }, value));
            } else label.append(element('span', { class: 'k9-variant-editor__text' }, value));
            label.append(element('span', { class: 'k9-variant-editor__note' }));
            options.append(label);
          });
          group.append(options);
          return group;
        });
        this.reason = element('p', { class: 'k9-variant-editor__reason', id: `${this.panel.id}-reason`, hidden: '' });
        this.validate = element('button', { type: 'button', class: 'k9-variant-editor__validate', 'data-variant-validate': '' }, this.config.text.validate);
        this.panel.replaceChildren(...nodes, this.reason, this.validate);
        this.refreshChoices();
      }
      choose(input) {
        if (!this.model || !input?.matches('input[type="radio"]')) return;
        this.selection[Number(input.dataset.option)] = input.value;
        this.refreshChoices();
      }
      refreshChoices() {
        const { names } = this.model;
        this.panel.querySelectorAll('fieldset').forEach((group, i) => {
          const legend = group.querySelector('legend');
          legend.replaceChildren(document.createTextNode(this.config.swatches?.[names[i]] ? `${names[i]} : ` : names[i]));
          if (this.config.swatches?.[names[i]]) legend.append(element('strong', {}, this.selection[i]));
          group.querySelectorAll('input').forEach(input => {
            const trial = [...this.selection]; trial[i] = input.value;
            const { reason } = resolve(this.model, trial, this.cart, this.config);
            const label = input.closest('label');
            label.querySelector('.k9-variant-editor__note').textContent = reason === 'unavailable' || reason === 'missing' ? this.config.text.unavailable : '';
            label.classList.toggle('is-unavailable', reason === 'unavailable' || reason === 'missing');
          });
        });
        this.updateValidate();
      }
      updateValidate() {
        if (!this.validate || !this.model) return;
        const { reason } = resolve(this.model, this.selection, this.cart, this.config);
        const blocked = reason === 'unavailable' || reason === 'missing' || reason === 'in-cart';
        this.validate.disabled = this.locked() || blocked;
        this.reason.hidden = !blocked;
        this.reason.textContent = reason === 'in-cart' ? this.config.text.inCart : blocked ? this.config.text.unavailable : '';
        if (blocked) this.validate.setAttribute('aria-describedby', this.reason.id); else this.validate.removeAttribute('aria-describedby');
      }
      async change() {
        const { variant, reason } = resolve(this.model, this.selection, this.cart, this.config);
        if (reason === 'current') { this.close(true); return; }
        if (reason || !variant) { this.updateValidate(); return; }
        if (state.pending || status.blocked || Base.cartIsBusy() || window.K9NativeGift?.checkoutBlocked) { this.show(this.config.text.wait, status.blocked); return; }
        const targetId = variant.id;
        const config = { ...this.config, variants: this.model.variants };
        state.pending = true; state.version++; this.setAttribute('aria-busy', 'true'); refresh();
        this.show(this.config.text.busy, false);
        const get = async url => { const r = await Base.json(url); if (!r.response.ok) throw Object.assign(Error('read'), { status: r.response.status }); return r.data; };
        let held = false;
        const result = await replace(config, targetId, {
          read: () => get(this.config.cartUrl),
          beforeMutation: () => {
            if (Base.cartIsBusy() || status.blocked || (!held && window.K9NativeGift?.checkoutBlocked)) throw Error('busy');
            if (!held) {
              window.K9NativeGift?.holdMutation();
              held = Boolean(window.K9NativeGift?.checkoutBlocked);
            }
          },
          add: item => Base.json(this.config.addUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: [item] }) }),
          remove: key => Base.json(this.config.changeUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity: 0 }) }),
        });
        status.blocked = result.posted && !result.success;
        if (result.posted && result.cart && document.querySelector('cart-items')) {
          // The main-cart form still has the old line indices. Keep the entire old
          // document locked until navigation; never redraw just its hidden drawer.
          if (result.success) {
            status.navigating = true; block(); this.show(this.config.text.busy, false);
            try { location.reload(); } catch { this.show(this.config.text.uncertain, true); this.message.focus(); }
          } else {
            // Keep the warning visible until explicit recovery, with every stale
            // mutation blocked. Reloading now would discard this ambiguity notice.
            block(); state.pending = false; state.version++; refresh(); this.removeAttribute('aria-busy');
            this.show(this.config.text.uncertain, true); this.message.focus();
          }
          return;
        }
        let rendered = false;
        try {
          if (result.cart && result.posted) rendered = await Base.prototype.redraw.call({ button: this.toggle }, result.cart, result.success ? targetId : null);
        } catch { /* A recovery link remains available when the HTML cannot be read. */ }
        if (result.posted && !rendered) status.blocked = true;
        state.pending = false; state.version++; refresh(); this.removeAttribute('aria-busy');
        const own = canonical(properties({ properties: this.config.properties }));
        const candidates = [...document.querySelectorAll('k9-cart-line-variant')].filter(e => e.config && canonical(properties({ properties: e.config.properties })) === own);
        // Nothing posted: the cart is unchanged, so the message belongs to this very line (it may have a twin).
        const current = !result.posted ? this
          : candidates.find(e => e.config.variantId === (result.success ? targetId : this.config.variantId)) || candidates.find(e => e.config.variantId === targetId) || this;
        const text = result.success && rendered ? this.config.text.updated : result.posted ? this.config.text.uncertain : this.config.text.beforeError;
        if (status.blocked) block();
        if (current.isConnected && current.toggle) {
          current.show(text, !result.success || !rendered);
          current.syncControls();
          if (!result.posted && current === this && this.model) { this.cart = result.cart || this.cart; this.refreshChoices(); }
          (result.success ? current.toggle : current.message).focus();
        } else {
          const error = document.getElementById('CartDrawer-CartErrors');
          if (error) { error.textContent = text; error.tabIndex = -1; const recovery = document.createElement('a'); recovery.href = this.recovery.href; recovery.textContent = this.recovery.textContent; error.append(recovery); error.focus(); }
        }
      }
    }
    customElements.define('k9-cart-line-variant', CartLineVariant);
  }
  register(); document.addEventListener('DOMContentLoaded', register, { once: true });
})();
